import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  DESCRIPTION, NAME, STANDARDS, STATUS, TASK_ASSIGNED_TO, VALUE_CHAIN_MAPS,
} from 'constants/formFields';
import useErrorMessage from 'hooks/useErrorMessage';
import i18n from 'i18n';
import { useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useLazyGetIssignUsersInfoQuery } from 'store/slices/tasksManagement/apis/tasksManagementApi';
import { useLazyGetValueChainMapDetailsQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { initiateFormSchema } from 'utils/validators';

import type { IGetValueChainMapDetailsResponse } from 'store/slices/valueChainMap/interfaces/IGetValueChainMapDetailsResponse';
import type { TInitiateFormSchema } from 'utils/validators';
import type IUseInitiateForm from '../interfaces/IUseInitiateForm';

const useInitiateForm = ({ recordId, handleStatusChange }: IUseInitiateForm) => {
  const fetchQuery = recordId ? {
    id: recordId,
  } : skipToken;
  const [getValueChainMapDetails, { error: fetchErrorVCDetail }] = useLazyGetValueChainMapDetailsQuery();
  const [getIssignUsersInfo, { data: usersInfo, error: fetchErrorUsersInfo }] = useLazyGetIssignUsersInfoQuery();

  const [VCMInfo, setVCMInfo] = useState<IGetValueChainMapDetailsResponse[]>([]);
  const loadedVCMIdsRef = useRef<string>('');

  const { data: singleMA, error: fetchErrorSingleMA } = useGetSingleMAInitiateQuery(fetchQuery);

  const errorMessage = useErrorMessage(fetchErrorVCDetail || fetchErrorUsersInfo || fetchErrorSingleMA);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const {
    name, description, valueChainMaps, teamMembers, reportingStandards, status,
  } = singleMA?.data || {};

  useEffect(() => {
    if (status) {
      handleStatusChange(status);
    }
  }, [status, handleStatusChange]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading, errors },
    reset,
    setError,
    getValues,
    watch,
    setValue,
  } = useForm<TInitiateFormSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(initiateFormSchema),
    values: {
      [NAME]: name || '',
      [DESCRIPTION]: description || '',
      [VALUE_CHAIN_MAPS]: VCMInfo,
      [TASK_ASSIGNED_TO]: !teamMembers || teamMembers.length === 0
        ? []
        : (usersInfo?.data ?? [])
          .filter((user) => teamMembers.some((m) => m.userId === user.id))
          .map((user) => ({
            value: `${user.id}`,
            label: `${user.firstName} ${user.lastName}`,
            role: user.role,
            position: teamMembers.find((member) => member.userId === user.id)?.position,
            email: user.email,
          })),
      [STANDARDS]: reportingStandards?.map((standard) => ({
        value: standard.id,
        label: standard.name || '',
      })) || [],
      [STATUS]: status || '',
    },
  });

  useEffect(() => {
    if (valueChainMaps && valueChainMaps.length > 0) {
      const valueChainMapIds = valueChainMaps.map((item) => item.id).filter((id): id is number => id !== undefined);
      const idsKey = valueChainMapIds.sort().join(',');
      if (loadedVCMIdsRef.current === idsKey) {
        return;
      }
      loadedVCMIdsRef.current = idsKey;
      setVCMInfo([]);
      valueChainMapIds.forEach((id) => {
        getValueChainMapDetails({ chainId: id.toString() }).then((response) => {
          const responseData = response.data?.data;
          if (responseData) {
            setVCMInfo((prev) => {
              const exists = prev.some((item) => item.id === responseData.id);
              if (exists) return prev;
              return [...prev, responseData];
            });
          }
        });
      });
    } else if (!valueChainMaps || valueChainMaps.length === 0) {
      if (loadedVCMIdsRef.current !== '') {
        loadedVCMIdsRef.current = '';
        setVCMInfo([]);
      }
    }
  }, [valueChainMaps, getValueChainMapDetails]);

  useEffect(() => {
    if (teamMembers && teamMembers?.length > 0) {
      const userIds = teamMembers?.map((user) => Number(user?.userId));
      if (userIds && userIds.length > 0) {
        getIssignUsersInfo({ userIds: Array.from(new Set(userIds)) });
      }
    }
  }, [usersInfo, getIssignUsersInfo, teamMembers]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    reset,
    errors,
  };
};

export default useInitiateForm;
